import { RealtimeQuote, TimeSeriesResponse, TechnicalIndicatorResponse, MarketDataRequest, TechnicalIndicatorRequest, LiveTickerItem, MarketSnapshot, PortfolioMarketData, SignalMarketContext, SymbolSearchResult, TwelveDataConfig, SymbolType, WebSocketConfig, WebSocketState, WebSocketPriceUpdate, WebSocketQuoteUpdate, CacheEntry, EnhancedCacheConfig,  } from '../types/market-data.types';

/**
 * Market Data Service with WebSocket Support, Enhanced Caching, Multi-Provider Fallback
 * Handles all live market data operations using TwelveData, Coinbase, CoinGecko, and Alpha Vantage APIs
 * Provider Priority: 
 * - Crypto: TwelveData → Coinbase → CoinGecko → Alpha Vantage
 * - Other assets: TwelveData → Alpha Vantage
 *
 * Features:
 * - WebSocket real-time streaming for reduced API calls
 * - Enhanced caching with stale-while-revalidate strategy
 * - Automatic reconnection and connection pooling
 * - Batch processing and rate limiting
 * - Strict 377 requests per minute rate limiting
 * - Multi-provider fallback for comprehensive market coverage
 */

class MarketDataService {
  private apiKey: string;
  private alphaVantageApiKey: string;
  private coinbaseApiKey: string;
  private coinbaseApiSecret: string;
  private coingeckoApiKey: string;
  private binanceApiKey: string;
  private baseURL: string = 'https://api.twelvedata.com';
  private alphaVantageBaseURL: string = 'https://www.alphavantage.co/query';
  private coinbaseBaseURL: string = 'https://api.coinbase.com/v2';
  private coingeckoBaseURL: string = 'https://api.coingecko.com/api/v3';
  private binanceBaseURL: string = 'https://api.binance.com';
  private wsURL: string = 'wss://ws.twelvedata.com/v1/quotes/price';

  // Enhanced Cache System
  private cache: Map<string, CacheEntry<any>>;
  private cacheConfig: EnhancedCacheConfig = {
    enabled: true,
    strategy: 'lru',
    maxSize: 500, // Increased from 200
    defaultTTL: 120000, // Increased to 2 minutes from 30 seconds
    staleWhileRevalidate: false, // Disabled to prevent background refreshes
    revalidateInterval: 300000, // Increased to 5 minutes
  };

  // WebSocket Management
  private ws: WebSocket | null = null;
  private wsState: WebSocketState = 'disconnected';
  private wsConfig: WebSocketConfig = {
    enabled: true,
    reconnect: true,
    reconnectInterval: 5000,
    maxReconnectAttempts: 10,
    heartbeatInterval: 30000,
  };
  private wsReconnectAttempts: number = 0;
  private wsSubscriptions: Set<string> = new Set();
  private wsCallbacks: Map<string, ((update: WebSocketPriceUpdate | WebSocketQuoteUpdate) => void)[]> = new Map();
  private wsHeartbeatTimer: NodeJS.Timeout | null = null;
  private wsReconnectTimer: NodeJS.Timeout | null = null;

  // Strict Rate Limiting System (377 requests per minute)
  private readonly MAX_REQUESTS_PER_MINUTE = 377;
  private readonly RATE_LIMIT_WINDOW_MS = 60000; // 1 minute
  private readonly MIN_REQUEST_INTERVAL_MS = Math.ceil(this.RATE_LIMIT_WINDOW_MS / this.MAX_REQUESTS_PER_MINUTE); // ~159ms
  private requestTimestamps: number[] = []; // Sliding window of request timestamps
  private pendingRequests: Map<string, Promise<any>> = new Map(); // Request deduplication
  private requestQueue: Array<{ resolve: Function; reject: Function; fn: Function }> = [];
  private isProcessingQueue: boolean = false;
  private lastRequestTime: number = 0;

  // Rate limit statistics
  private rateLimitStats = {
    totalRequests: 0,
    rejectedRequests: 0,
    queuedRequests: 0,
    averageWaitTime: 0,
  };

  // =====================================
  // EXPONENTIAL BACKOFF RETRY SYSTEM
  // =====================================

  private retryConfig = {
    maxRetries: 5,
    initialDelay: 100, // Start with 100ms
    maxDelay: 10000, // Cap at 10 seconds
    backoffMultiplier: 2.5, // Exponential growth factor
    jitterFactor: 0.1, // Add randomness to prevent thundering herd
  };

  // Circuit breaker to prevent excessive retries
  private circuitBreaker = {
    failureThreshold: 10, // Open circuit after 10 consecutive failures
    resetTimeout: 60000, // Reset circuit after 1 minute
    consecutiveFailures: 0,
    lastFailureTime: 0,
    state: 'CLOSED' as 'CLOSED' | 'OPEN' | 'HALF_OPEN',
  };

  /**
   * Calculate exponential backoff delay with jitter
   * Prevents thundering herd problem and adds randomness
   */
  private calculateBackoffDelay(attempt: number): number {
    const baseDelay = Math.min(
      this.retryConfig.initialDelay * Math.pow(this.retryConfig.backoffMultiplier, attempt),
      this.retryConfig.maxDelay
    );

    // Add jitter: random value between 90% and 110% of base delay
    const jitter = baseDelay * this.retryConfig.jitterFactor;
    const randomJitter = (Math.random() * 2 - 1) * jitter;

    return Math.floor(baseDelay + randomJitter);
  }

  /**
   * Check circuit breaker state
   * Returns true if requests are allowed, false if circuit is open
   */
  private checkCircuitBreaker(): boolean {
    const now = Date.now();

    // If circuit is open, check if reset timeout has passed
    if (this.circuitBreaker.state === 'OPEN') {
      if (now - this.circuitBreaker.lastFailureTime >= this.circuitBreaker.resetTimeout) {
        console.log('🔄 Circuit breaker transitioning to HALF_OPEN state');
        this.circuitBreaker.state = 'HALF_OPEN';
        return true;
      }
      console.warn('⛔ Circuit breaker is OPEN - blocking requests');
      return false;
    }

    return true;
  }

  /**
   * Record circuit breaker failure
   */
  private recordCircuitBreakerFailure(): void {
    this.circuitBreaker.consecutiveFailures++;
    this.circuitBreaker.lastFailureTime = Date.now();

    if (this.circuitBreaker.consecutiveFailures >= this.circuitBreaker.failureThreshold) {
      console.error(`🚨 Circuit breaker OPENED after ${this.circuitBreaker.failureThreshold} consecutive failures`);
      this.circuitBreaker.state = 'OPEN';
    }
  }

  /**
   * Record circuit breaker success
   */
  private recordCircuitBreakerSuccess(): void {
    if (this.circuitBreaker.state === 'HALF_OPEN') {
      console.log('✅ Circuit breaker CLOSED after successful request');
      this.circuitBreaker.state = 'CLOSED';
    }
    this.circuitBreaker.consecutiveFailures = 0;
  }

  /**
   * Reset circuit breaker manually
   */
  public resetCircuitBreaker(): void {
    this.circuitBreaker.consecutiveFailures = 0;
    this.circuitBreaker.state = 'CLOSED';
    this.circuitBreaker.lastFailureTime = 0;
    console.log('✅ Circuit breaker manually reset');
  }

  /**
   * Execute API request with exponential backoff retry logic
   * Handles transient failures with progressive delays
   */
  private async executeWithRetry<T>(
    requestFn: () => Promise<T>,
    context: string,
    options?: { maxRetries?: number; skipCircuitBreaker?: boolean }
  ): Promise<T> {
    const maxRetries = options?.maxRetries ?? this.retryConfig.maxRetries;
    const skipCircuitBreaker = options?.skipCircuitBreaker ?? false;

    // Check circuit breaker
    if (!skipCircuitBreaker && !this.checkCircuitBreaker()) {
      throw new Error(`Circuit breaker is OPEN for ${context}. System is temporarily unavailable.`);
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Execute the request
        const result = await requestFn();

        // Record success for circuit breaker
        if (!skipCircuitBreaker) {
          this.recordCircuitBreakerSuccess();
        }

        // Log retry success if this wasn't the first attempt
        if (attempt > 0) {
          console.log(`✅ ${context} succeeded after ${attempt} ${attempt === 1 ? 'retry' : 'retries'}`);
        }

        return result;
      } catch (error: any) {
        lastError = error;

        // Don't retry on certain errors
        const isRetryableError = this.isRetryableError(error);

        if (!isRetryableError || attempt === maxRetries) {
          // Record failure for circuit breaker
          if (!skipCircuitBreaker) {
            this.recordCircuitBreakerFailure();
          }

          console.error(
            `❌ ${context} failed after ${attempt} ${attempt === 1 ? 'attempt' : 'attempts'}: ${error.message}`
          );
          throw error;
        }

        // Calculate backoff delay
        const delay = this.calculateBackoffDelay(attempt);

        console.warn(
          `⚠️ ${context} attempt ${attempt + 1}/${maxRetries + 1} failed: ${error.message}. ` +
          `Retrying in ${delay}ms...`
        );

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // This should never be reached, but TypeScript needs it
    throw lastError || new Error(`${context} failed after ${maxRetries} retries`);
  }

  /**
   * Determine if an error is retryable
   * Network errors, timeouts, and rate limits are retryable
   * Authentication and validation errors are not
   */
  private isRetryableError(error: any): boolean {
    const errorMessage = error.message?.toLowerCase() || '';

    // Retryable errors - expanded to include more network-related patterns
    const retryablePatterns = [
      'network', 'timeout', 'rate limit', 'too many requests',
      'service unavailable', 'bad gateway', 'gateway timeout',
      'connection', 'econnrefused', 'etimedout', 'enotfound',
      'symbol', 'figi', // TwelveData symbol errors should trigger fallback providers
      'load failed', 'failed to fetch', 'fetch failed', // Browser fetch errors
      'cors', 'network error', 'net::', 'dns' // Additional network patterns
    ];

    // Non-retryable errors (but now more specific)
    const nonRetryablePatterns = [
      'unauthorized', 'forbidden', 'authentication',
      'bad request', 'api key' // Made more specific - removed generic "invalid"
    ];

    // Check non-retryable patterns first
    if (nonRetryablePatterns.some(pattern => errorMessage.includes(pattern))) {
      return false;
    }

    // Check retryable patterns
    if (retryablePatterns.some(pattern => errorMessage.includes(pattern))) {
      return true;
    }

    // Check error type/name
    const errorType = error.name?.toLowerCase() || '';
    const networkErrorTypes = ['typeerror', 'networkerror', 'fetcherror'];
    if (networkErrorTypes.some(type => errorType.includes(type))) {
      return true;
    }

    // Default: retry on HTTP status codes 429, 500, 502, 503, 504
    const status = error.status || error.statusCode;
    return status === 429 || (status >= 500 && status < 600);
  }

  constructor(config?: Partial<TwelveDataConfig & { wsConfig?: Partial<WebSocketConfig>; cacheConfig?: Partial<EnhancedCacheConfig> }>) {
    this.apiKey = config?.apiKey || process.env.NEXT_PUBLIC_TWELVEDATA_API_KEY || '';
    this.alphaVantageApiKey = process.env.NEXT_PUBLIC_ALPHAVANTAGE_API_KEY || '';
    this.coinbaseApiKey = process.env.NEXT_PUBLIC_COINBASE_API_KEY || '';
    this.coinbaseApiSecret = process.env.NEXT_PUBLIC_COINBASE_API_SECRET || '';
    this.coingeckoApiKey = process.env.NEXT_PUBLIC_COINGECKO_API_KEY || '';
    this.binanceApiKey = process.env.NEXT_PUBLIC_BINANCE_API_KEY || '';

    if (!this.apiKey) {
      console.warn('⚠️ TwelveData API key not found. Falling back to other providers.');
    }

    if (!this.coinbaseApiKey || !this.coinbaseApiSecret) {
      console.warn('⚠️ Coinbase API credentials not found. Crypto fallback limited.');
    }

    if (!this.coingeckoApiKey) {
      console.log('ℹ️ CoinGecko API key not configured. Using free tier with rate limits.');
    }

    // Apply custom WebSocket config
    if (config?.wsConfig) {
      this.wsConfig = { ...this.wsConfig, ...config.wsConfig };
    }

    // Apply custom cache config
    if (config?.cacheConfig) {
      this.cacheConfig = { ...this.cacheConfig, ...config.cacheConfig };
    }

    this.cache = new Map();

    // ✅ CRITICAL FIX: Do NOT initialize WebSocket automatically
    // WebSocket will ONLY be initialized lazily when subscribeToSymbol() is called
    // This prevents:
    // - Unnecessary WebSocket connections when not needed
    // - Authentication errors on startup
    // - Server-side rendering issues
    // - Automatic reconnection loops with invalid credentials
    
    console.log('✅ Market data service initialized. WebSocket will connect only when needed.');

    // Start cache cleanup interval
    this.startCacheCleanup();
  }

  // =====================================
  // USER API KEYS MANAGEMENT
  // =====================================

  /**
   * Load user-specific API keys from database
   * This method should be called after user authentication
   */
  public async loadUserApiKeys(userId: string): Promise<void> {
    try {
      const { apiKeysService } = await import('./api-keys.service');

      // Load all active API keys for user
      const { data: apiKeys, error } = await apiKeysService.getUserApiKeys();

      if (error || !apiKeys) {
        console.warn('Could not load user API keys, using environment defaults');
        return;
      }

      // Override with user-specific keys where available
      apiKeys.forEach(key => {
        if (!key.is_active) return;

        switch (key.provider) {
          case 'COINBASE':
            if (key.api_key && key.api_secret) {
              this.coinbaseApiKey = key.api_key;
              this.coinbaseApiSecret = key.api_secret;
              console.log('✅ Using user-specific Coinbase API keys');
            }
            break;

          case 'TWELVE_DATA':
            if (key.api_key) {
              this.apiKey = key.api_key;
              console.log('✅ Using user-specific TwelveData API key');
            }
            break;

          case 'ALPHA_VANTAGE':
            if (key.api_key) {
              this.alphaVantageApiKey = key.api_key;
              console.log('✅ Using user-specific Alpha Vantage API key');
            }
            break;

          case 'COINGECKO':
            if (key.api_key) {
              this.coingeckoApiKey = key.api_key;
              console.log('✅ Using user-specific CoinGecko API key');
            }
            break;
        }
      });
    } catch (error) {
      console.error('Error loading user API keys:', error);
      // Continue with environment defaults
    }
  }

  /**
   * Get Coinbase API credentials
   * Returns user-specific keys if available, otherwise environment defaults
   */
  public getCoinbaseCredentials(): { apiKey: string; apiSecret: string } | null {
    if (this.coinbaseApiKey && this.coinbaseApiSecret) {
      return {
        apiKey: this.coinbaseApiKey,
        apiSecret: this.coinbaseApiSecret,
      };
    }
    return null;
  }

  /**
   * Check if user has configured Coinbase API keys
   */
  public hasCoinbaseCredentials(): boolean {
    return !!(this.coinbaseApiKey && this.coinbaseApiSecret);
  }

  // =====================================
  // WEBSOCKET CONNECTION MANAGEMENT
  // =====================================

  /**
   * Initialize WebSocket connection
   */
  private async initializeWebSocket(): Promise<void> {
    if (typeof window === 'undefined') {
      // Skip WebSocket initialization on server-side
      console.log('⏭️ Skipping WebSocket initialization on server-side');
      return;
    }

    // ✅ CRITICAL: Check if API key is available AND valid before attempting connection
    if (!this.apiKey) {
      console.warn('⚠️ TwelveData API key not configured. WebSocket connection disabled.');
      this.wsState = 'disconnected';
      this.wsConfig.enabled = false; // Disable WebSocket functionality
      return;
    }

    // ✅ Enhanced API key validation
    if (this.apiKey.length < 20 || !this.apiKey.match(/^[a-zA-Z0-9]+$/)) {
      console.error('❌ Invalid TwelveData API key format. WebSocket connection disabled.');
      console.error('Expected format: Alphanumeric string with at least 20 characters');
      this.wsState = 'error';
      this.wsConfig.enabled = false;
      return;
    }

    // ✅ Check if already connecting or connected
    if (this.wsState === 'connecting' || this.wsState === 'connected') {
      console.log('WebSocket already connecting or connected. Skipping initialization.');
      return;
    }

    try {
      this.wsState = 'connecting';
      console.log('🔌 Initializing TwelveData WebSocket connection...');

      const wsUrl = `${this.wsURL}?apikey=${this.apiKey}`;
      this.ws = new WebSocket(wsUrl);

      // Set connection timeout
      const connectionTimeout = setTimeout(() => {
        if (this.wsState === 'connecting') {
          console.error('❌ WebSocket connection timeout (10 seconds)');
          console.error('Possible causes: Invalid API key, network issues, or TwelveData server unavailable');
          this.ws?.close();
          this.wsState = 'error';
          this.wsConfig.enabled = false; // Disable to prevent retry loops
        }
      }, 10000); // 10 second timeout

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('✅ TwelveData WebSocket connected successfully');
        this.wsState = 'connected';
        this.wsReconnectAttempts = 0;

        // Resubscribe to all symbols
        if (this.wsSubscriptions.size > 0) {
          console.log(`📡 Resubscribing to ${this.wsSubscriptions.size} symbols`);
          this.subscribeWebSocket(Array.from(this.wsSubscriptions));
        }

        // Start heartbeat
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (event: Event) => {
        clearTimeout(connectionTimeout);
        
        // Enhanced error logging with detailed context
        const wsEvent = event as ErrorEvent;
        
        let errorDetails = {
          type: 'WebSocket Error',
          timestamp: new Date().toISOString(),
          readyState: this.ws?.readyState,
          readyStateText: this.getReadyStateText(this.ws?.readyState),
          wsUrl: this.wsURL,
          reconnectAttempts: this.wsReconnectAttempts,
          maxReconnectAttempts: this.wsConfig.maxReconnectAttempts,
          message: wsEvent.message || 'WebSocket connection error',
          error: wsEvent.error || 'No error details available',
        };

        console.error('❌ WebSocket Error Details:', errorDetails);

        // ✅ CRITICAL: Provide actionable error messages
        if (!this.apiKey) {
          console.error('🔑 WebSocket failed: Missing TwelveData API key.');
          console.error('Solution: Add NEXT_PUBLIC_TWELVEDATA_API_KEY to your .env file');
        } else if (this.apiKey.length < 20) {
          console.error('🔑 WebSocket failed: API key appears to be invalid (too short).');
          console.error('Solution: Verify your TwelveData API key is correct');
        }

        this.wsState = 'error';
        
        // ✅ CRITICAL: Disable WebSocket after first error to prevent infinite loops
        this.wsConfig.enabled = false;
        console.warn('⚠️ WebSocket has been disabled. Use REST API fallback for market data.');
      };

      this.ws.onclose = (event: CloseEvent) => {
        clearTimeout(connectionTimeout);
        
        // Enhanced close event logging
        const closeDetails = {
          type: 'WebSocket Closed',
          timestamp: new Date().toISOString(),
          code: event.code,
          reason: event.reason || 'No reason provided',
          wasClean: event.wasClean,
          reconnectAttempts: this.wsReconnectAttempts,
          maxReconnectAttempts: this.wsConfig.maxReconnectAttempts,
        };

        console.log('📊 WebSocket Close Details:', closeDetails);

        this.wsState = 'disconnected';
        this.stopHeartbeat();

        // ✅ Enhanced close code handling with actionable messages
        if (event.code === 1000) {
          console.log('✅ WebSocket closed normally (code 1000)');
        } else if (event.code === 1006) {
          console.error('❌ WebSocket closed abnormally (code 1006 - abnormal closure)');
          console.error('This typically indicates:');
          console.error('  1. Invalid or missing TwelveData API key');
          console.error('  2. API key quota exceeded');
          console.error('  3. Network connectivity issues');
          console.error('  4. TwelveData server rejected the connection');
          console.error('');
          console.error('Solution: Verify your API key and check TwelveData account status');
          console.error('The application will continue using REST API fallback for market data.');
          
          // ✅ CRITICAL: Permanently disable WebSocket on 1006 errors
          this.wsConfig.enabled = false;
          return; // Don't attempt reconnection
        } else if (event.code === 1008) {
          console.error('❌ WebSocket closed due to policy violation (code 1008)');
          console.error('This usually means the API key is invalid or has insufficient permissions');
          this.wsConfig.enabled = false; // Disable permanently
          return; // Don't attempt reconnection
        } else if (event.code === 4001 || event.code === 4003) {
          console.error('❌ WebSocket closed: Authentication failed (code 4001/4003)');
          console.error('Your TwelveData API key is invalid or expired');
          this.wsConfig.enabled = false; // Disable permanently
          return; // Don't attempt reconnection
        } else {
          console.warn(`⚠️ WebSocket closed with code ${event.code}: ${event.reason || 'Unknown reason'}`);
        }

        // ✅ ONLY attempt reconnection if:
        // - Reconnection is enabled in config
        // - WebSocket hasn't been disabled due to errors
        // - Haven't exceeded max reconnection attempts
        // - Close code is not an authentication error
        if (this.wsConfig.reconnect && 
            this.wsConfig.enabled &&
            this.wsReconnectAttempts < this.wsConfig.maxReconnectAttempts &&
            event.code !== 4001 && 
            event.code !== 4003 &&
            event.code !== 1006 &&
            event.code !== 1008) {
          this.scheduleReconnect();
        } else {
          console.log('WebSocket will not reconnect. Using REST API fallback for market data.');
          this.wsState = 'disconnected';
          this.wsConfig.enabled = false;
        }
      };
    } catch (error) {
      console.error('❌ Error initializing WebSocket:', error);
      this.wsState = 'error';
      this.wsConfig.enabled = false;
    }
  }

  /**
   * Get human-readable WebSocket ready state
   */
  private getReadyStateText(readyState?: number): string {
    if (readyState === undefined) return 'UNKNOWN';
    
    switch (readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING (0)';
      case WebSocket.OPEN: return 'OPEN (1)';
      case WebSocket.CLOSING: return 'CLOSING (2)';
      case WebSocket.CLOSED: return 'CLOSED (3)';
      default: return `UNKNOWN (${readyState})`;
    }
  }

  /**
   * Handle WebSocket messages
   */
  private handleWebSocketMessage(data: any): void {
    if (!data || !data.event) return;

    // Handle different message types
    if (data.event === 'price' && data.symbol) {
      const update: WebSocketQuoteUpdate = {
        type: 'quote',
        event: 'price',
        symbol: data.symbol,
        price: parseFloat(data.price),
        timestamp: data.timestamp || Date.now(),
        change: data.change ? parseFloat(data.change) : undefined,
        percent_change: data.percent_change ? parseFloat(data.percent_change) : undefined,
      };

      // Update cache with WebSocket data
      this.updateCacheFromWebSocket(data.symbol, update);

      // Notify all subscribers
      const callbacks = this.wsCallbacks.get(data.symbol) || [];
      callbacks.forEach(callback => callback(update));
    } else if (data.event === 'heartbeat') {
      console.log('Heartbeat received from server');
    } else if (data.event === 'subscribe-status') {
      console.log('Subscription status:', data);
    }
  }

  /**
   * Update cache with WebSocket data
   */
  private updateCacheFromWebSocket(symbol: string, update: WebSocketQuoteUpdate): void {
    const cacheKey = `quote_${symbol}`;
    const cached = this.cache.get(cacheKey);

    if (cached && cached.data) {
      // Update existing cache entry
      const updatedQuote: RealtimeQuote = {
        ...cached.data,
        price: update.price || cached.data.price,
        close: update.price || cached.data.close,
        change: update.change || cached.data.change,
        percent_change: update.percent_change || cached.data.percent_change,
        timestamp: update.timestamp || Date.now(),
      };

      this.setCachedData(cacheKey, updatedQuote, this.cacheConfig.defaultTTL);
    }
  }

  /**
   * Subscribe to symbols via WebSocket
   */
  private subscribeWebSocket(symbols: string[]): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected. Cannot subscribe.');
      return;
    }

    const formattedSymbols = symbols.map(s => this.formatSymbolForAPI(s));

    const subscribeMessage = {
      action: 'subscribe',
      params: {
        symbols: formattedSymbols.join(','),
      },
    };

    this.ws.send(JSON.stringify(subscribeMessage));

    formattedSymbols.forEach(symbol => this.wsSubscriptions.add(symbol));
  }

  /**
   * Unsubscribe from symbols via WebSocket
   */
  private unsubscribeWebSocket(symbols: string[]): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const formattedSymbols = symbols.map(s => this.formatSymbolForAPI(s));

    const unsubscribeMessage = {
      action: 'unsubscribe',
      params: {
        symbols: formattedSymbols.join(','),
      },
    };

    this.ws.send(JSON.stringify(unsubscribeMessage));

    formattedSymbols.forEach(symbol => this.wsSubscriptions.delete(symbol));
  }

  /**
   * Start heartbeat mechanism
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.wsHeartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: 'heartbeat' }));
      }
    }, this.wsConfig.heartbeatInterval);
  }

  /**
   * Stop heartbeat mechanism
   */
  private stopHeartbeat(): void {
    if (this.wsHeartbeatTimer) {
      clearInterval(this.wsHeartbeatTimer);
      this.wsHeartbeatTimer = null;
    }
  }

  /**
   * Schedule WebSocket reconnection
   */
  private scheduleReconnect(): void {
    if (this.wsReconnectTimer) return;

    this.wsState = 'reconnecting';
    this.wsReconnectAttempts++;

    console.log(`Reconnecting WebSocket (attempt ${this.wsReconnectAttempts}/${this.wsConfig.maxReconnectAttempts})...`);

    this.wsReconnectTimer = setTimeout(() => {
      this.wsReconnectTimer = null;
      this.initializeWebSocket();
    }, this.wsConfig.reconnectInterval);
  }

  /**
   * Public method to subscribe to real-time updates
   * Lazy WebSocket initialization - only initialize when first subscription is made
   */
  public subscribeToSymbol(
    symbol: string,
    callback: (update: WebSocketPriceUpdate | WebSocketQuoteUpdate) => void
  ): () => void {
    const formattedSymbol = this.formatSymbolForAPI(symbol);

    // Add callback
    if (!this.wsCallbacks.has(formattedSymbol)) {
      this.wsCallbacks.set(formattedSymbol, []);
    }
    this.wsCallbacks.get(formattedSymbol)!.push(callback);

    // Lazy WebSocket initialization - only initialize when first subscription is made
    if (this.wsConfig.enabled && !this.ws && this.apiKey && typeof window !== 'undefined') {
      console.log('🔌 Lazy initializing WebSocket for first subscription');
      this.initializeWebSocket();
    }

    // Subscribe via WebSocket if connected
    if (this.wsState === 'connected') {
      this.subscribeWebSocket([symbol]);
    } else if (this.wsState === 'connecting') {
      // Wait for connection before subscribing
      const checkConnection = setInterval(() => {
        if (this.wsState === 'connected') {
          clearInterval(checkConnection);
          this.subscribeWebSocket([symbol]);
        } else if (this.wsState === 'error' || this.wsState === 'disconnected') {
          clearInterval(checkConnection);
          console.warn(`⚠️ Cannot subscribe to ${symbol} - WebSocket not connected`);
        }
      }, 100);

      // Clear interval after 10 seconds to prevent memory leak
      setTimeout(() => clearInterval(checkConnection), 10000);
    }

    // Return unsubscribe function
    return () => {
      const callbacks = this.wsCallbacks.get(formattedSymbol);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }

        // If no more callbacks, unsubscribe
        if (callbacks.length === 0) {
          this.wsCallbacks.delete(formattedSymbol);
          this.unsubscribeWebSocket([symbol]);
        }
      }
    };
  }

  /**
   * Get WebSocket connection state
   */
  public getWebSocketState(): WebSocketState {
    return this.wsState;
  }

  /**
   * Check if WebSocket is connected
   */
  public isWebSocketConnected(): boolean {
    return this.wsState === 'connected';
  }

  // =====================================
  // ENHANCED CACHE MANAGEMENT
  // =====================================

  /**
   * Get cached data with staleness check
   */
  private getCachedData<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) return null;

    const now = Date.now();
    const age = now - entry.timestamp;

    // Update access statistics
    entry.accessCount++;
    entry.lastAccessed = now;

    // Check if data is fresh
    if (age < entry.ttl) {
      entry.isStale = false;
      return entry.data;
    }

    // Data is stale - return it anyway to reduce API calls
    // Only mark as stale, don't trigger refresh automatically
    entry.isStale = true;
    return entry.data;
  }

  /**
   * Set cached data with TTL
   */
  private setCachedData<T>(key: string, data: T, ttl?: number): void {
    const now = Date.now();
    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      accessCount: 0,
      lastAccessed: now,
      ttl: ttl || this.cacheConfig.defaultTTL,
      isStale: false,
    };

    this.cache.set(key, entry);

    // Apply cache eviction if size exceeds limit
    if (this.cache.size > this.cacheConfig.maxSize) {
      this.evictCache();
    }
  }

  /**
   * Trigger background refresh for stale data - DISABLED
   */
  private triggerBackgroundRefresh(key: string): void {
    // Disabled to prevent excessive API calls
    // Cache will be refreshed on next explicit request
    return;
  }

  /**
   * Evict cache entries based on strategy
   */
  private evictCache(): void {
    const entries = Array.from(this.cache.entries());

    let keyToEvict: string | null = null;

    switch (this.cacheConfig.strategy) {
      case 'lru': // Least Recently Used
        keyToEvict = entries.reduce((oldest, [key, entry]) => {
          const oldestEntry = this.cache.get(oldest);
          return !oldestEntry || entry.lastAccessed < oldestEntry.lastAccessed ? key : oldest;
        }, entries[0][0]);
        break;

      case 'lfu': // Least Frequently Used
        keyToEvict = entries.reduce((least, [key, entry]) => {
          const leastEntry = this.cache.get(least);
          return !leastEntry || entry.accessCount < leastEntry.accessCount ? key : least;
        }, entries[0][0]);
        break;

      case 'fifo': // First In First Out
        keyToEvict = entries.reduce((oldest, [key, entry]) => {
          const oldestEntry = this.cache.get(oldest);
          return !oldestEntry || entry.timestamp < oldestEntry.timestamp ? key : oldest;
        }, entries[0][0]);
        break;
    }

    if (keyToEvict) {
      this.cache.delete(keyToEvict);
    }
  }

  /**
   * Start periodic cache cleanup
   */
  private startCacheCleanup(): void {
    setInterval(() => {
      const now = Date.now();
      const keysToDelete: string[] = [];

      this.cache.forEach((entry, key) => {
        const age = now - entry.timestamp;
        if (age > entry.ttl * 2) { // Remove entries twice as old as TTL
          keysToDelete.push(key);
        }
      });

      keysToDelete.forEach(key => this.cache.delete(key));

      if (keysToDelete.length > 0) {
        console.log(`Cleaned up ${keysToDelete.length} stale cache entries`);
      }
    }, this.cacheConfig.revalidateInterval);
  }

  /**
   * Clear all cached data
   */
  private clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  public getCacheStats(): {
    size: number;
    maxSize: number;
    hitRate: number;
    staleEntries: number;
  } {
    let staleCount = 0;
    let totalAccess = 0;

    this.cache.forEach(entry => {
      if (entry.isStale) staleCount++;
      totalAccess += entry.accessCount;
    });

    return {
      size: this.cache.size,
      maxSize: this.cacheConfig.maxSize,
      hitRate: this.cache.size > 0 ? totalAccess / this.cache.size : 0,
      staleEntries: staleCount,
    };
  }

  // =====================================
  // STRICT RATE LIMITING SYSTEM
  // =====================================

  /**
   * Check if we can make a request without exceeding rate limit
   */
  private canMakeRequest(): boolean {
    const now = Date.now();
    const windowStart = now - this.RATE_LIMIT_WINDOW_MS;

    // Remove timestamps outside the current window
    this.requestTimestamps = this.requestTimestamps.filter(timestamp => timestamp > windowStart);

    // Check if we're under the limit
    return this.requestTimestamps.length < this.MAX_REQUESTS_PER_MINUTE;
  }

  /**
   * Get current rate limit status
   */
  public getRateLimitStatus(): {
    requestsInWindow: number;
    maxRequests: number;
    remainingRequests: number;
    windowResetIn: number;
    isAtLimit: boolean;
    stats: { totalRequests: number; rejectedRequests: number; queuedRequests: number; averageWaitTime: number };
  } {
    const now = Date.now();
    const windowStart = now - this.RATE_LIMIT_WINDOW_MS;

    // Clean up old timestamps
    this.requestTimestamps = this.requestTimestamps.filter(timestamp => timestamp > windowStart);

    const oldestTimestamp = this.requestTimestamps[0] || now;
    const timeUntilOldestExpires = Math.max(0, (oldestTimestamp + this.RATE_LIMIT_WINDOW_MS) - now);

    return {
      requestsInWindow: this.requestTimestamps.length,
      maxRequests: this.MAX_REQUESTS_PER_MINUTE,
      remainingRequests: Math.max(0, this.MAX_REQUESTS_PER_MINUTE - this.requestTimestamps.length),
      windowResetIn: timeUntilOldestExpires,
      isAtLimit: this.requestTimestamps.length >= this.MAX_REQUESTS_PER_MINUTE,
      stats: { ...this.rateLimitStats },
    };
  }

  /**
   * Wait for rate limit availability
   */
  private async waitForRateLimit(): Promise<void> {
    while (!this.canMakeRequest()) {
      const status = this.getRateLimitStatus();
      const waitTime = Math.max(100, status.windowResetIn);

      console.warn(
        `⚠️ Rate limit reached (${status.requestsInWindow}/${status.maxRequests}). ` +
        `Waiting ${waitTime}ms for window to reset...`
      );

      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    // Ensure minimum interval between requests
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL_MS) {
      const additionalWait = this.MIN_REQUEST_INTERVAL_MS - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, additionalWait));
    }
  }

  /**
   * Record a request timestamp
   */
  private recordRequest(): void {
    const now = Date.now();
    this.requestTimestamps.push(now);
    this.lastRequestTime = now;
    this.rateLimitStats.totalRequests++;

    // Log rate limit status periodically
    if (this.rateLimitStats.totalRequests % 50 === 0) {
      const status = this.getRateLimitStatus();
      console.log(
        `📊 Rate Limit Status: ${status.requestsInWindow}/${status.maxRequests} requests in window, ` +
        `${status.remainingRequests} remaining`
      );
    }
  }

  /**
   * Process queued requests with rate limiting
   */
  private async processRequestQueue(): Promise<void> {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      // Wait for rate limit availability
      await this.waitForRateLimit();

      const queueItem = this.requestQueue.shift();
      if (!queueItem) break;

      try {
        const result = await queueItem.fn();
        queueItem.resolve(result);
      } catch (error) {
        queueItem.reject(error);
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Queue a request with rate limiting
   */
  private async queueRequest<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ resolve, reject, fn });
      this.rateLimitStats.queuedRequests++;
      this.processRequestQueue();
    });
  }

  // =====================================
  // SYMBOL FORMATTING HELPER
  // =====================================

  /**
   * Formats symbol for TwelveData API
   * TwelveData expects symbols with slashes for crypto and forex (e.g., BTC/USD, EUR/USD)
   */
  private formatSymbolForAPI(symbol: string): string {
    // Check if it's a commodity and needs mapping
    if (this.isCommoditySymbol(symbol)) {
      return this.mapCommoditySymbol(symbol);
    }

    // TwelveData API expects symbols with slashes (e.g., BTC/USD, EUR/USD)
    // No formatting needed - return as-is
    return symbol;
  }

  /**
   * Format symbol for Polygon.io API
   * Polygon expects uppercase symbols without slashes (e.g., BTCUSD instead of BTC/USD)
   */
  private formatSymbolForPolygon(symbol: string): string {
    // Remove slashes and convert to uppercase
    return symbol.replace(/\//g, '').toUpperCase();
  }

  /**
   * Format symbol for Alpha Vantage API
   * Alpha Vantage expects uppercase symbols without slashes (e.g., AAPL, EUR/USD becomes EURUSD)
   */
  private formatSymbolForAlphaVantage(symbol: string): string {
    // Remove slashes and convert to uppercase
    return symbol.replace(/\//g, '').toUpperCase();
  }

  /**
   * Format symbol for Binance API
   * Binance expects uppercase symbols without slashes (e.g., BTCUSDT instead of BTC/USD)
   */
  private formatSymbolForBinance(symbol: string): string {
    // Convert BTC/USD to BTCUSDT format
    // Handle format: BASE/QUOTE → BASEQUOTE
    const parts = symbol.split('/');
    if (parts.length === 2) {
      const base = parts[0].toUpperCase();
      let quote = parts[1].toUpperCase();

      // For crypto pairs with USD quote, Binance uses USDT (Tether stablecoin)
      if (quote === 'USD') {
        return base + 'USDT';
      }

      // For other pairs, combine as-is
      return base + quote;
    }
    // If no slash, assume it's already in correct format
    return symbol.toUpperCase();
  }

  /**
   * Format symbol for Coinbase API
   * Coinbase expects format: BTC-USD instead of BTC/USD
   */
  private formatSymbolForCoinbase(symbol: string): string {
    return symbol.replace(/\//g, '-').toUpperCase();
  }

  /**
   * Format symbol for CoinGecko API
   * CoinGecko uses coin IDs (bitcoin, ethereum) instead of symbols
   */
  private formatSymbolForCoinGecko(symbol: string): string {
    const symbolMap: Record<string, string> = {
      'BTC': 'bitcoin',
      'ETH': 'ethereum',
      'XRP': 'ripple',
      'ADA': 'cardano',
      'SOL': 'solana',
      'DOT': 'polkadot',
      'LINK': 'chainlink',
      'UNI': 'uniswap',
      'MATIC': 'matic-network',
      'AVAX': 'avalanche-2',
      'ATOM': 'cosmos',
      'LTC': 'litecoin',
      'BCH': 'bitcoin-cash',
      'XLM': 'stellar',
      'ALGO': 'algorand',
      'VET': 'vechain',
      'ICP': 'internet-computer',
      'FIL': 'filecoin',
      'TRX': 'tron',
      'ETC': 'ethereum-classic',
      'XMR': 'monero',
      'EOS': 'eos',
      'AAVE': 'aave',
      'MKR': 'maker',
      'THETA': 'theta-token',
      'AXS': 'axie-infinity',
      'SAND': 'the-sandbox',
      'MANA': 'decentraland',
      'NEAR': 'near',
      'FTM': 'fantom',
      'SHIB': 'shiba-inu',
      'DOGE': 'dogecoin',
      'APT': 'aptos',
      'ARB': 'arbitrum',
      'OP': 'optimism',
      'PEPE': 'pepe',
      'IMX': 'immutable-x',
      'LDO': 'lido-dao',
      'STX': 'blockstack',
      'INJ': 'injective-protocol',
    };

    const baseSymbol = symbol.split('/')[0].toUpperCase();
    return symbolMap[baseSymbol] || baseSymbol.toLowerCase();
  }

  /**
   * Detects if a symbol is cryptocurrency
   */
  private isCryptoSymbol(symbol: string): boolean {
    // Comprehensive list of major cryptocurrencies
    const cryptoSymbols = [
      'BTC', 'ETH', 'XRP', 'ADA', 'SOL', 'DOT', 'LINK', 'UNI', 'MATIC', 'AVAX',
      'ATOM', 'LTC', 'BCH', 'XLM', 'ALGO', 'VET', 'ICP', 'FIL', 'TRX', 'ETC',
      'XMR', 'EOS', 'AAVE', 'MKR', 'THETA', 'AXS', 'SAND', 'MANA', 'NEAR', 'FTM',
      'SHIB', 'DOGE', 'APT', 'ARB', 'OP', 'PEPE', 'IMX', 'LDO', 'STX', 'INJ'
    ];
    const baseSymbol = symbol.split('/')[0].toUpperCase();
    return cryptoSymbols.includes(baseSymbol);
  }

  /**
   * Detects if a symbol is a forex pair (24/5 trading)
   * Forex pairs trade 24 hours Monday-Friday, closed on weekends
   */
  private isForexSymbol(symbol: string): boolean {
    // Common forex pairs with major currencies
    const forexCurrencies = [
      'EUR', 'USD', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'NZD',
      'SEK', 'NOK', 'DKK', 'PLN', 'TRY', 'ZAR', 'MXN', 'BRL',
      'SGD', 'HKD', 'CNY', 'INR'
    ];
    const parts = symbol.split('/');

    if (parts.length === 2) {
      const base = parts[0].toUpperCase();
      let quote = parts[1].toUpperCase();

      // Both base and quote must be valid forex currencies
      return forexCurrencies.includes(base) && forexCurrencies.includes(quote);
    }

    return false;
  }

  /**
   * Detects if a symbol is a commodity
   * Commodities include metals, energy, and agricultural products
   */
  private isCommoditySymbol(symbol: string): boolean {
    // Precious metals supported by TwelveData in forex-style pairs
    const commoditySymbols = [
      'XAU', 'XAG', 'XPT', 'XPD', // Precious metals (Gold, Silver, Platinum, Palladium)
    ];

    const baseSymbol = symbol.split('/')[0].toUpperCase();
    return commoditySymbols.includes(baseSymbol);
  }

  /**
   * Map commodity symbols to TwelveData-compatible formats
   * TwelveData supports precious metals in forex-style pairs (XAU/USD, XAG/USD)
   */
  private mapCommoditySymbol(symbol: string): string {
    const commodityMappings: Record<string, string> = {
      'XAU/USD': 'XAU/USD', // Gold - supported by TwelveData
      'XAG/USD': 'XAG/USD', // Silver - supported by TwelveData
      'XPT/USD': 'XPT/USD', // Platinum - supported by TwelveData
      'XPD/USD': 'XPD/USD', // Palladium - supported by TwelveData
    };

    return commodityMappings[symbol.toUpperCase()] || symbol;
  }

  /**
   * Map symbol to CoinGecko coin ID
   * CoinGecko uses coin IDs (bitcoin, ethereum) instead of symbols
   */
  private mapSymbolToCoinGeckoId(symbol: string): string {
    const symbolMap: Record<string, string> = {
      'BTC': 'bitcoin',
      'ETH': 'ethereum',
      'XRP': 'ripple',
      'ADA': 'cardano',
      'SOL': 'solana',
      'DOT': 'polkadot',
      'LINK': 'chainlink',
      'UNI': 'uniswap',
      'MATIC': 'matic-network',
      'AVAX': 'avalanche-2',
      'ATOM': 'cosmos',
      'LTC': 'litecoin',
      'BCH': 'bitcoin-cash',
      'XLM': 'stellar',
      'ALGO': 'algorand',
      'VET': 'vechain',
      'ICP': 'internet-computer',
      'FIL': 'filecoin',
      'TRX': 'tron',
      'ETC': 'ethereum-classic',
      'XMR': 'monero',
      'EOS': 'eos',
      'AAVE': 'aave',
      'MKR': 'maker',
      'THETA': 'theta-token',
      'AXS': 'axie-infinity',
      'SAND': 'the-sandbox',
      'MANA': 'decentraland',
      'NEAR': 'near',
      'FTM': 'fantom',
      'SHIB': 'shiba-inu',
      'DOGE': 'dogecoin',
      'APT': 'aptos',
      'ARB': 'arbitrum',
      'OP': 'optimism',
      'PEPE': 'pepe',
      'IMX': 'immutable-x',
      'LDO': 'lido-dao',
      'STX': 'blockstack',
      'INJ': 'injective-protocol',
    };

    const baseSymbol = symbol.split('/')[0].toUpperCase();
    return symbolMap[baseSymbol] || baseSymbol.toLowerCase();
  }

  /**
   * Checks if current time is during forex trading hours (Monday-Friday, 24/5)
   * Forex market is closed on weekends
   */
  private isForexMarketOpen(): boolean {
    const now = new Date();
    const dayOfWeek = now.getUTCDay(); // 0 = Sunday, 6 = Saturday

    // Forex market is closed on Saturday (6) and Sunday (0)
    // Open Monday (1) through Friday (5), 24 hours per day
    return dayOfWeek >= 1 && dayOfWeek <= 5;
  }

  /**
   * Checks if commodity markets are open
   * Most commodity markets trade during specific hours on weekdays
   */
  private isCommodityMarketOpen(): boolean {
    const now = new Date();
    const dayOfWeek = now.getUTCDay(); // 0 = Sunday, 6 = Saturday
    const hour = now.getUTCHours();

    // Commodity markets typically closed on weekends
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return false;
    }

    // Most commodity markets trade during weekday business hours
    // Simplified check: 6 AM - 9 PM UTC (covers major trading sessions)
    return hour >= 6 && hour <= 21;
  }

  /**
   * Checks if market data should be displayed
   * Handles different symbol types: crypto (24/7), forex (24/5), stocks (market hours), commodities
   */
  private shouldShowMarketData(symbol: string, isMarketOpen?: boolean): boolean {
    // Cryptocurrency trades 24/7 - always show data
    if (this.isCryptoSymbol(symbol)) {
      return true;
    }

    // Forex trades 24/5 (Monday-Friday) - check if it's a weekday
    if (this.isForexSymbol(symbol)) {
      return this.isForexMarketOpen();
    }

    // Commodities have specific trading hours
    if (this.isCommoditySymbol(symbol)) {
      return this.isCommodityMarketOpen();
    }

    // For stocks and other instruments, use the API's market open flag
    return isMarketOpen ?? false;
  }

  // =====================================
  // API REQUEST HELPERS
  // =====================================

  /**
   * API request helper with strict rate limiting
   */
  private async makeRequest(endpoint: string, params: Record<string, any> = {}): Promise<any> {
    if (!this.apiKey) {
      throw new Error('TwelveData API key is not configured');
    }

    // Queue the request to ensure rate limiting
    return this.queueRequest(async () => {
      // Wait for rate limit availability (double check)
      await this.waitForRateLimit();

      if (params.symbol) {
        params.symbol = this.formatSymbolForAPI(params.symbol);
      }

      const queryParams = new URLSearchParams({
        apikey: this.apiKey,
        ...params,
      });

      let url = `${this.baseURL}${endpoint}?${queryParams.toString()}`;

      try {
        // Record the request
        this.recordRequest();

        const requestStart = Date.now();
        const response = await fetch(url);
        const requestDuration = Date.now() - requestStart;

        // Update average wait time
        this.rateLimitStats.averageWaitTime =
          (this.rateLimitStats.averageWaitTime * (this.rateLimitStats.totalRequests - 1) + requestDuration) /
          this.rateLimitStats.totalRequests;

        if (!response.ok) {
          // Check for rate limit errors
          if (response.status === 429) {
            console.error('🚨 Rate limit exceeded! TwelveData returned 429');
            this.rateLimitStats.rejectedRequests++;
            throw new Error('Rate limit exceeded. Please wait before making more requests.');
          }
          throw new Error(`TwelveData API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.status === 'error') {
          throw new Error(`TwelveData API error: ${data.message || 'Unknown error'}`);
        }

        return data;
      } catch (error) {
        console.error('TwelveData API request failed:', error);
        throw error;
      }
    });
  }

  // =====================================
  // ALPHA VANTAGE INTEGRATION
  // =====================================

  /**
   * Get real-time quote from Alpha Vantage API with retry logic
   * Handles stocks, forex, and some crypto symbols
   */
  private async getAlphaVantageQuote(symbol: string): Promise<RealtimeQuote> {
    if (!this.alphaVantageApiKey) {
      throw new Error('Alpha Vantage API key is not configured');
    }

    const isForex = this.isForexSymbol(symbol);
    const isCrypto = this.isCryptoSymbol(symbol);
    const isCommodity = this.isCommoditySymbol(symbol);
    const formattedSymbol = this.formatSymbolForAlphaVantage(symbol);

    return this.executeWithRetry(
      async () => {
        let url: string;
        let functionType: string;

        // Determine API function based on symbol type
        if (isCrypto) {
          // Crypto: Use CURRENCY_EXCHANGE_RATE or DIGITAL_CURRENCY_DAILY
          const baseCurrency = symbol.split('/')[0];
          const quoteCurrency = symbol.split('/')[1] || 'USD';
          url = `${this.alphaVantageBaseURL}?function=CURRENCY_EXCHANGE_RATE&from_currency=${baseCurrency}&to_currency=${quoteCurrency}&apikey=${this.alphaVantageApiKey}`;
          functionType = 'CRYPTO';
        } else if (isForex) {
          // Forex: Use CURRENCY_EXCHANGE_RATE
          const baseCurrency = symbol.split('/')[0];
          const quoteCurrency = symbol.split('/')[1];
          url = `${this.alphaVantageBaseURL}?function=CURRENCY_EXCHANGE_RATE&from_currency=${baseCurrency}&to_currency=${quoteCurrency}&apikey=${this.alphaVantageApiKey}`;
          functionType = 'FOREX';
        } else if (isCommodity) {
          // Commodities: Alpha Vantage doesn't have direct commodity support
          // Try GLOBAL_QUOTE with futures symbol format
          const mappedSymbol = this.mapCommoditySymbol(symbol);
          url = `${this.alphaVantageBaseURL}?function=GLOBAL_QUOTE&symbol=${mappedSymbol}&apikey=${this.alphaVantageApiKey}`;
          functionType = 'COMMODITY';
        } else {
          // Stock: Use GLOBAL_QUOTE
          url = `${this.alphaVantageBaseURL}?function=GLOBAL_QUOTE&symbol=${formattedSymbol}&apikey=${this.alphaVantageApiKey}`;
          functionType = 'STOCK';
        }

        console.log(`Fetching ${symbol} from Alpha Vantage API (type: ${functionType})`);

        const response = await fetch(url);

        if (!response.ok) {
          const error: any = new Error(`Alpha Vantage API error: ${response.status} ${response.statusText}`);
          error.status = response.status;
          throw error;
        }

        const data = await response.json();

        // Check for API errors
        if (data['Error Message']) {
          throw new Error(`Alpha Vantage API error: ${data['Error Message']}`);
        }

        if (data['Note']) {
          // Rate limit message
          throw new Error('Alpha Vantage rate limit reached. Please wait before making more requests.');
        }

        // Parse response based on function type
        let quote: RealtimeQuote;

        if ((functionType === 'STOCK' || functionType === 'COMMODITY') && data['Global Quote']) {
          const globalQuote = data['Global Quote'];
          const currentPrice = parseFloat(globalQuote['05. price'] || '0');
          const previousClose = parseFloat(globalQuote['08. previous close'] || '0');
          const change = parseFloat(globalQuote['09. change'] || '0');
          const percentChange = parseFloat(globalQuote['10. change percent']?.replace('%', '') || '0');
          const open = parseFloat(globalQuote['02. open'] || '0');
          const high = parseFloat(globalQuote['03. high'] || '0');
          const low = parseFloat(globalQuote['04. low'] || '0');
          const volume = parseInt(globalQuote['06. volume'] || '0');

          quote = {
            symbol: symbol,
            name: symbol,
            exchange: 'ALPHA_VANTAGE',
            currency: 'USD',
            datetime: globalQuote['07. latest trading day'] || new Date().toISOString(),
            timestamp: Date.now(),
            open: open,
            high: high,
            low: low,
            close: currentPrice,
            price: currentPrice,
            volume: volume,
            previous_close: previousClose,
            change: change,
            percent_change: percentChange,
            average_volume: volume,
            is_market_open: functionType === 'COMMODITY' ? this.isCommodityMarketOpen() : true,
            fifty_two_week: undefined,
          };
        } else if ((functionType === 'FOREX' || functionType === 'CRYPTO') && data['Realtime Currency Exchange Rate']) {
          const exchangeRate = data['Realtime Currency Exchange Rate'];
          const currentPrice = parseFloat(exchangeRate['5. Exchange Rate'] || '0');
          const bid = parseFloat(exchangeRate['8. Bid Price'] || currentPrice);
          const ask = parseFloat(exchangeRate['9. Ask Price'] || currentPrice);

          quote = {
            symbol: symbol,
            name: symbol,
            exchange: 'ALPHA_VANTAGE',
            currency: exchangeRate['3. To_Currency Code'] || 'USD',
            datetime: exchangeRate['6. Last Refreshed'] || new Date().toISOString(),
            timestamp: Date.now(),
            open: currentPrice,
            high: ask,
            low: bid,
            close: currentPrice,
            price: currentPrice,
            volume: 0, // Not provided by Alpha Vantage for forex/crypto
            previous_close: currentPrice,
            change: 0,
            percent_change: 0,
            average_volume: 0,
            is_market_open: functionType === 'CRYPTO' || this.isForexMarketOpen(),
            fifty_two_week: undefined,
          };
        } else {
          throw new Error(`No valid data returned for symbol ${symbol} from Alpha Vantage`);
        }

        console.log(`✅ Successfully fetched ${symbol} from Alpha Vantage API`);
        return quote;
      },
      `Alpha Vantage API request for ${symbol}`
    );
  }

  // =====================================
  // BINANCE INTEGRATION
  // =====================================

  /**
   * Get real-time crypto quote from Binance API with retry logic
   * Handles symbol format conversion (BTC/USD to BTCUSDT)
   */
  private async getBinanceCryptoQuote(symbol: string): Promise<RealtimeQuote> {
    if (!this.binanceApiKey) {
      throw new Error('Binance API key is not configured');
    }

    const formattedSymbol = this.formatSymbolForBinance(symbol);

    return this.executeWithRetry(
      async () => {
        let url = `${this.binanceBaseURL}/api/v3/ticker/24hr?symbol=${formattedSymbol}`;

        console.log(`Fetching crypto ${symbol} from Binance API (formatted as ${formattedSymbol})`);

        try {
          const response = await fetch(url, {
            headers: {
              'X-MBX-APIKEY': this.binanceApiKey,
            },
          });

          if (!response.ok) {
            if (response.status === 404) {
              throw new Error(`Crypto symbol ${symbol} (${formattedSymbol}) not found on Binance`);
            }
            const error: any = new Error(`Binance API error: ${response.status} ${response.statusText}`);
            error.status = response.status;
            throw error;
          }

          const data = await response.json();

          if (!data.symbol) {
            throw new Error(`No data returned for crypto symbol ${symbol}`);
          }

          const currentPrice = parseFloat(data.lastPrice);
          const previousClose = parseFloat(data.prevClosePrice);
          const change = parseFloat(data.priceChange);
          const percentChange = parseFloat(data.priceChangePercent);
          const open = parseFloat(data.openPrice);
          const high = parseFloat(data.highPrice);
          const low = parseFloat(data.lowPrice);
          const volume = parseFloat(data.volume);

          let quote: RealtimeQuote = {
            symbol: symbol,
            name: symbol,
            exchange: 'BINANCE',
            currency: 'USD',
            datetime: new Date(data.closeTime).toISOString(),
            timestamp: data.closeTime,
            open: open,
            high: high,
            low: low,
            close: currentPrice,
            price: currentPrice,
            volume: volume,
            previous_close: previousClose,
            change: change,
            percent_change: percentChange,
            average_volume: volume,
            is_market_open: true,
            fifty_two_week: undefined,
          };

          console.log(`✅ Successfully fetched ${symbol} from Binance API`);
          return quote;
        } catch (fetchError: any) {
          // Enhanced error wrapping for network-level failures with CORS detection
          const isCorsError = fetchError.message?.toLowerCase().includes('cors') ||
                             fetchError.message?.includes('Failed to fetch') ||
                             fetchError.name === 'TypeError';
          
          const isLoadFailed = fetchError.message?.includes('Load failed');
          
          if (isCorsError || isLoadFailed) {
            const networkError: any = new Error(
              `❌ Browser security restriction: Cannot access Binance API directly from browser for ${symbol}. ` +
              `This is a CORS (Cross-Origin Resource Sharing) limitation. ` +
              `Recommendation: Use a server-side proxy or API gateway for production. ` +
              `Current status: Using cached data or placeholder values.`
            );
            networkError.name = 'CORSError';
            networkError.originalError = fetchError;
            networkError.isCorsError = true;
            networkError.isNetworkError = true;
            networkError.requiresServerProxy = true; // Flag for server-side proxy requirement
            throw networkError;
          }
          throw fetchError;
        }
      },
      `Binance API request for ${symbol}`,
      { maxRetries: 2 } // Reduce retries for CORS errors since they won't succeed
    );
  }

  // =====================================
  // COINBASE INTEGRATION
  // =====================================

  // Note: formatSymbolForCoinbase is defined earlier in the class

  /**
   * Get real-time crypto quote from Coinbase API with retry logic
   * Coinbase provides professional-grade crypto market data
   */
  private async getCoinbaseCryptoQuote(symbol: string): Promise<RealtimeQuote> {
    if (!this.coinbaseApiKey) {
      throw new Error('Coinbase API key is not configured');
    }

    const formattedSymbol = this.formatSymbolForCoinbase(symbol);

    return this.executeWithRetry(
      async () => {
        // Coinbase API: Get spot price and 24hr stats
        const [priceResponse, statsResponse] = await Promise.all([
          fetch(`${this.coinbaseBaseURL}/prices/${formattedSymbol}/spot`, {
            headers: {
              'CB-ACCESS-KEY': this.coinbaseApiKey,
            },
          }),
          fetch(`${this.coinbaseBaseURL}/prices/${formattedSymbol}/buy`, {
            headers: {
              'CB-ACCESS-KEY': this.coinbaseApiKey,
            },
          }),
        ]);

        if (!priceResponse.ok) {
          if (priceResponse.status === 404) {
            throw new Error(`Crypto symbol ${symbol} (${formattedSymbol}) not found on Coinbase`);
          }
          const error: any = new Error(`Coinbase API error: ${priceResponse.status} ${priceResponse.statusText}`);
          error.status = priceResponse.status;
          throw error;
        }

        const priceData = await priceResponse.json();
        const statsData = statsResponse.ok ? await statsResponse.json() : null;

        if (!priceData?.data?.amount) {
          throw new Error(`No data returned for crypto symbol ${symbol} from Coinbase`);
        }

        const currentPrice = parseFloat(priceData.data.amount);
        const buyPrice = statsData?.data?.amount ? parseFloat(statsData.data.amount) : currentPrice;

        // Note: Coinbase's free tier doesn't provide 24h change, volume, etc.
        // For comprehensive data, use Coinbase Pro API
        let quote: RealtimeQuote = {
          symbol: symbol,
          name: symbol,
          exchange: 'COINBASE',
          currency: priceData.data.currency || 'USD',
          datetime: new Date().toISOString(),
          timestamp: Date.now(),
          open: currentPrice,
          high: buyPrice,
          low: currentPrice,
          close: currentPrice,
          price: currentPrice,
          volume: 0, // Not available in basic Coinbase API
          previous_close: currentPrice,
          change: 0, // Not available in basic Coinbase API
          percent_change: 0, // Not available in basic Coinbase API
          average_volume: 0,
          is_market_open: true, // Crypto markets are always open
          fifty_two_week: undefined,
        };

        console.log(`✅ Successfully fetched ${symbol} from Coinbase API`);
        return quote;
      },
      `Coinbase API request for ${symbol}`,
      { maxRetries: 2 }
    );
  }

  // =====================================
  // COINGECKO INTEGRATION
  // =====================================

  // Note: mapSymbolToCoinGeckoId is defined earlier in the class

  /**
   * Get real-time crypto quote from CoinGecko API with retry logic
   * CoinGecko provides comprehensive cryptocurrency data
   */
  private async getCoinGeckoCryptoQuote(symbol: string): Promise<RealtimeQuote> {
    const coinId = this.mapSymbolToCoinGeckoId(symbol);
    const vsCurrency = symbol.split('/')[1]?.toLowerCase() || 'usd';

    return this.executeWithRetry(
      async () => {
        // Build URL with optional API key
        let url = `${this.coingeckoBaseURL}/simple/price?ids=${coinId}&vs_currencies=${vsCurrency}&include_24hr_change=true&include_24hr_vol=true&include_last_updated_at=true`;
        
        const headers: Record<string, string> = {};
        if (this.coingeckoApiKey) {
          headers['x-cg-pro-api-key'] = this.coingeckoApiKey;
        }

        console.log(`Fetching crypto ${symbol} from CoinGecko API (coin ID: ${coinId})`);

        const response = await fetch(url, { headers });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(`Crypto symbol ${symbol} (${coinId}) not found on CoinGecko`);
          }
          const error: any = new Error(`CoinGecko API error: ${response.status} ${response.statusText}`);
          error.status = response.status;
          throw error;
        }

        const data = await response.json();

        if (!data[coinId]) {
          throw new Error(`No data returned for crypto symbol ${symbol} (${coinId}) from CoinGecko`);
        }

        const coinData = data[coinId];
        const currentPrice = coinData[vsCurrency];
        const change24h = coinData[`${vsCurrency}_24h_change`] || 0;
        const volume24h = coinData[`${vsCurrency}_24h_vol`] || 0;
        const lastUpdated = coinData.last_updated_at ? coinData.last_updated_at * 1000 : Date.now();

        const previousClose = currentPrice / (1 + change24h / 100);
        const changeAmount = currentPrice - previousClose;

        let quote: RealtimeQuote = {
          symbol: symbol,
          name: symbol,
          exchange: 'COINGECKO',
          currency: vsCurrency.toUpperCase(),
          datetime: new Date(lastUpdated).toISOString(),
          timestamp: lastUpdated,
          open: previousClose,
          high: currentPrice,
          low: previousClose,
          close: currentPrice,
          price: currentPrice,
          volume: volume24h,
          previous_close: previousClose,
          change: changeAmount,
          percent_change: change24h,
          average_volume: volume24h,
          is_market_open: true, // Crypto markets are always open
          fifty_two_week: undefined,
        };

        console.log(`✅ Successfully fetched ${symbol} from CoinGecko API`);
        return quote;
      },
      `CoinGecko API request for ${symbol}`,
      { maxRetries: 2 }
    );
  }

  /**
   * Create an enhanced placeholder quote with retry suggestion
   */
  private createPlaceholderQuote(symbol: string, errorReason: string, isRetryable: boolean = false): RealtimeQuote {
    console.warn(`Creating placeholder quote for ${symbol}. Reason: ${errorReason}`);

    return {
      symbol: symbol,
      name: symbol,
      exchange: 'UNAVAILABLE',
      currency: 'USD',
      datetime: new Date().toISOString(),
      timestamp: Date.now(),
      open: 0,
      high: 0,
      low: 0,
      close: 0,
      price: 0,
      volume: 0,
      previous_close: 0,
      change: 0,
      percent_change: 0,
      average_volume: 0,
      is_market_open: false,
      fifty_two_week: undefined,
      error: errorReason,
      isRetryable: isRetryable,
      retryMessage: isRetryable
        ? 'Market data temporarily unavailable. The system will automatically retry.' :'Market data unavailable. Please check your API configuration.',
    };
  }

  /**
   * Get real-time quote with enhanced multi-provider fallback logic
   * Priority: 
   * - Crypto: TwelveData → Coinbase → CoinGecko → Alpha Vantage
   * - Other assets: TwelveData → Alpha Vantage
   */
  async getRealtimeQuote(symbol: string): Promise<RealtimeQuote> {
    const cacheKey = `quote_${symbol}`;

    return this.deduplicateRequest(cacheKey, async () => {
      const cached = this.getCachedData<RealtimeQuote>(cacheKey);

      if (cached && !cached.error) {
        console.log(`Cache hit for ${symbol}`);
        return cached;
      }

      let lastError: Error | null = null;
      let isRetryable = false;
      const isCrypto = this.isCryptoSymbol(symbol);
      const isCommodity = this.isCommoditySymbol(symbol);

      // Special handling for commodities
      if (isCommodity) {
        console.log(`⚠️ Detected commodity symbol ${symbol}. Commodity support may be limited.`);
      }

      // Special handling for crypto - enhanced fallback chain
      if (isCrypto) {
        console.log(`💰 Detected crypto symbol ${symbol}. Will use enhanced crypto provider fallback chain.`);
      }

      // Priority 1: Try TwelveData first
      if (this.apiKey) {
        try {
          console.log(`Fetching ${symbol} from TwelveData API (Priority 1)${isCommodity ? ' - Commodity' : ''}${isCrypto ? ' - Crypto' : ''}`);

          const response = await this.executeWithRetry(
            () => this.makeRequest('/quote', { symbol }),
            `TwelveData quote for ${symbol}`,
            { maxRetries: isCommodity ? 1 : (isCrypto ? 1 : 2) }
          );

          let quote: RealtimeQuote = {
            symbol: response.symbol || symbol,
            name: response.name || symbol,
            exchange: response.exchange,
            currency: response.currency,
            datetime: response.datetime,
            timestamp: response.timestamp
              ? (response.timestamp < 4102444800 ? response.timestamp * 1000 : response.timestamp)
              : Date.now(),
            open: parseFloat(response.open || '0'),
            high: parseFloat(response.high || '0'),
            low: parseFloat(response.low || '0'),
            close: parseFloat(response.close || '0'),
            price: parseFloat(response.close || '0'),
            volume: parseInt(response.volume || '0'),
            previous_close: parseFloat(response.previous_close || '0'),
            change: parseFloat(response.change || '0'),
            percent_change: parseFloat(response.percent_change || '0'),
            average_volume: parseInt(response.average_volume || '0'),
            is_market_open: response.is_market_open ?? (this.isCryptoSymbol(symbol) || this.isCommodityMarketOpen()),
            fifty_two_week: response.fifty_two_week,
          };

          const ttl = this.isCryptoSymbol(symbol) ? 300000 : 600000;
          this.setCachedData(cacheKey, quote, ttl);

          console.log(`✅ Successfully fetched ${symbol} from TwelveData (Primary)`);
          return quote;
        } catch (twelveDataError: any) {
          lastError = twelveDataError;
          isRetryable = this.isRetryableError(twelveDataError);
          const errorMessage = twelveDataError.message || String(twelveDataError);

          if (isCrypto) {
            console.warn(`⚠️ TwelveData failed for crypto ${symbol}: ${errorMessage}. ` +
              `Trying Coinbase fallback...`);
          } else if (isCommodity) {
            console.warn(`⚠️ TwelveData failed for commodity ${symbol}: ${errorMessage}. ` +
              `Trying Alpha Vantage...`);
          } else {
            console.warn(`⚠️ TwelveData failed for ${symbol}: ${errorMessage}. Trying Alpha Vantage...`);
          }
        }
      }

      // Priority 2: Try Coinbase for crypto
      if (this.coinbaseApiKey && isCrypto) {
        try {
          console.log(`Attempting Coinbase fallback for crypto symbol ${symbol} (Priority 2)`);

          const coinbaseQuote = await this.getCoinbaseCryptoQuote(symbol);

          const ttl = 300000;
          this.setCachedData(cacheKey, coinbaseQuote, ttl);

          console.log(`✅ Successfully fetched ${symbol} from Coinbase fallback`);
          return coinbaseQuote;
        } catch (coinbaseError: any) {
          lastError = coinbaseError;
          isRetryable = this.isRetryableError(coinbaseError);
          const coinbaseErrorMessage = coinbaseError.message || String(coinbaseError);
          console.warn(`⚠️ Coinbase fallback failed for ${symbol}: ${coinbaseErrorMessage}. ` +
            `Trying CoinGecko...`);
        }
      }

      // Priority 3: Try CoinGecko for crypto
      if (isCrypto) {
        try {
          console.log(`Attempting CoinGecko fallback for crypto symbol ${symbol} (Priority 3)`);

          const coingeckoQuote = await this.getCoinGeckoCryptoQuote(symbol);

          const ttl = 300000;
          this.setCachedData(cacheKey, coingeckoQuote, ttl);

          console.log(`✅ Successfully fetched ${symbol} from CoinGecko fallback`);
          return coingeckoQuote;
        } catch (coingeckoError: any) {
          lastError = coingeckoError;
          isRetryable = this.isRetryableError(coingeckoError);
          const coingeckoErrorMessage = coingeckoError.message || String(coingeckoError);
          console.warn(`⚠️ CoinGecko fallback failed for ${symbol}: ${coingeckoErrorMessage}. ` +
            `Trying Alpha Vantage...`);
        }
      }

      // Priority 4: Try Alpha Vantage (for all asset types including crypto as last resort)
      if (this.alphaVantageApiKey) {
        try {
          console.log(`Attempting Alpha Vantage fallback for ${symbol} (Priority 4)`);

          const alphaVantageQuote = await this.getAlphaVantageQuote(symbol);

          const ttl = this.isCryptoSymbol(symbol) ? 300000 : 600000;
          this.setCachedData(cacheKey, alphaVantageQuote, ttl);

          console.log(`✅ Successfully fetched ${symbol} from Alpha Vantage fallback`);
          return alphaVantageQuote;
        } catch (alphaVantageError: any) {
          lastError = alphaVantageError;
          isRetryable = this.isRetryableError(alphaVantageError);
          const alphaVantageErrorMessage = alphaVantageError.message || String(alphaVantageError);
          console.warn(`⚠️ Alpha Vantage fallback failed for ${symbol}: ${alphaVantageErrorMessage}`);
        }
      }

      // All providers failed - create enhanced placeholder
      let errorContext = 'All market data providers failed';
      if (isCrypto && !this.coinbaseApiKey && !this.coingeckoApiKey) {
        errorContext = 'TwelveData failed and no crypto provider credentials configured. Add NEXT_PUBLIC_COINBASE_API_KEY or NEXT_PUBLIC_COINGECKO_API_KEY to .env';
      } else if (!this.apiKey && !this.alphaVantageApiKey && !this.coinbaseApiKey && !this.coingeckoApiKey) {
        errorContext = 'No API keys configured. Please add API keys to .env';
      }

      const errorMessage = lastError?.message || errorContext;
      const placeholderQuote = this.createPlaceholderQuote(
        symbol,
        errorMessage,
        isRetryable
      );

      const cacheDuration = isRetryable ? 10000 : 30000;
      this.setCachedData(cacheKey, placeholderQuote, cacheDuration);

      console.error(`❌ Returning placeholder for ${symbol}. Reason: ${errorMessage}`);
      return placeholderQuote;
    });
  }

  /**
   * Get real-time quotes for multiple symbols (batch)
   */
  async getBatchQuotes(symbols: string[]): Promise<RealtimeQuote[]> {
    const uniqueSymbols = Array.from(new Set(symbols));
    const quotes: RealtimeQuote[] = [];

    // Check cache first for all symbols
    const uncachedSymbols: string[] = [];
    for (const symbol of uniqueSymbols) {
      const cacheKey = `quote_${symbol}`;
      const cached = this.getCachedData<RealtimeQuote>(cacheKey);

      if (cached) {
        quotes.push(cached);
        console.log(`Using cached data for ${symbol}`);
      } else {
        uncachedSymbols.push(symbol);
      }
    }

    // Only fetch uncached symbols
    if (uncachedSymbols.length > 0) {
      console.log(`Fetching ${uncachedSymbols.length} symbols from API`);

      // Process in smaller batches with longer delays
      const batchSize = 2; // Reduced from 5 to 2
      for (let i = 0; i < uncachedSymbols.length; i += batchSize) {
        const batch = uncachedSymbols.slice(i, i + batchSize);
        const batchPromises = batch.map(symbol => this.getRealtimeQuote(symbol));
        const batchResults = await Promise.allSettled(batchPromises);

        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            quotes.push(result.value);
          } else {
            console.error(`Failed to fetch quote for ${batch[index]}:`, result.reason);
          }
        });

        // Longer delay between batches (1 second instead of 200ms)
        if (i + batchSize < uncachedSymbols.length) {
          console.log('Waiting 1 second before next batch to respect rate limits...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    return quotes;
  }

  // =====================================
  // TIME SERIES DATA
  // =====================================

  /**
   * Get time series data for a symbol
   */
  async getTimeSeries(params: MarketDataRequest): Promise<TimeSeriesResponse> {
    const cacheKey = `timeseries_${params.symbol}_${params.interval}_${params.outputsize}`;
    const cached = this.getCachedData<TimeSeriesResponse>(cacheKey);
    if (cached) return cached;

    try {
      const response = await this.makeRequest('/time_series', {
        symbol: params.symbol,
        interval: params.interval || '1day',
        outputsize: params.outputsize || 30,
        timezone: params.timezone || 'America/New_York',
        start_date: params.start_date,
        end_date: params.end_date,
      });

      this.setCachedData(cacheKey, response);
      return response;
    } catch (error) {
      console.error(`Error fetching time series for ${params.symbol}:`, error);
      throw new Error(`Failed to fetch time series for ${params.symbol}`);
    }
  }

  // =====================================
  // TECHNICAL INDICATORS
  // =====================================

  /**
   * Get technical indicator data
   */
  async getTechnicalIndicator(params: TechnicalIndicatorRequest): Promise<TechnicalIndicatorResponse> {
    const cacheKey = `indicator_${params.indicator}_${params.symbol}_${params.interval}`;
    const cached = this.getCachedData<TechnicalIndicatorResponse>(cacheKey);
    if (cached) return cached;

    try {
      const requestParams: any = {
        symbol: params.symbol,
        interval: params.interval || '1day',
        outputsize: params.outputsize || 30,
        time_period: params.time_period || 14,
        series_type: params.series_type || 'close',
      };

      // Add additional indicator-specific parameters
      Object.keys(params).forEach(key => {
        if (!['symbol', 'interval', 'outputsize', 'time_period', 'series_type', 'indicator'].includes(key)) {
          requestParams[key] = params[key];
        }
      });

      const response = await this.makeRequest(`/${params.indicator}`, requestParams);

      this.setCachedData(cacheKey, response);
      return response;
    } catch (error) {
      console.error(`Error fetching ${params.indicator} for ${params.symbol}:`, error);
      throw new Error(`Failed to fetch ${params.indicator} for ${params.symbol}`);
    }
  }

  /**
   * Get multiple technical indicators for a symbol
   */
  async getMultipleIndicators(
    symbol: string,
    indicators: TechnicalIndicatorRequest[]
  ): Promise<Map<string, TechnicalIndicatorResponse>> {
    const results = new Map<string, TechnicalIndicatorResponse>();

    for (const indicatorRequest of indicators) {
      try {
        const data = await this.getTechnicalIndicator({
          ...indicatorRequest,
          symbol,
        });
        results.set(indicatorRequest.indicator, data);
      } catch (error) {
        console.error(`Failed to fetch ${indicatorRequest.indicator}:`, error);
      }
    }

    return results;
  }

  // =====================================
  // SYMBOL SEARCH
  // =====================================

  /**
   * Search for symbols
   */
  async searchSymbols(query: string, limit: number = 10): Promise<SymbolSearchResult[]> {
    try {
      const response = await this.makeRequest('/symbol_search', {
        symbol: query,
        outputsize: limit,
      });

      return response.data || [];
    } catch (error) {
      console.error(`Error searching symbols for "${query}":`, error);
      throw new Error(`Failed to search symbols`);
    }
  }

  // =====================================
  // LIVE DASHBOARD DATA
  // =====================================

  /**
   * Get live ticker data for dashboard
   */
  async getLiveTickerData(symbols: string[]): Promise<LiveTickerItem[]> {
    const quotes = await this.getBatchQuotes(symbols);

    return quotes.map(quote => {
      const isCrypto = this.isCryptoSymbol(quote.symbol);
      const showData = this.shouldShowMarketData(quote.symbol, quote.is_market_open);

      return {
        pair: quote.symbol,
        price: showData ? quote.price : 0,
        change: showData ? (quote.change || 0) : 0,
        changePercent: showData ? (quote.percent_change || 0) : 0,
        isPositive: (quote.change || 0) >= 0,
        volume: showData ? quote.volume : 0,
        high24h: showData ? quote.high : 0,
        low24h: showData ? quote.low : 0,
        timestamp: quote.timestamp,
        isMarketOpen: quote.is_market_open,
        isCrypto,
      };
    });
  }

  /**
   * Get market snapshot for a symbol
   */
  async getMarketSnapshot(symbol: string, symbolType: SymbolType): Promise<MarketSnapshot> {
    let quote = await this.getRealtimeQuote(symbol);

    return {
      symbol: quote.symbol,
      symbolType,
      name: quote.name,
      price: quote.price,
      change: quote.change || 0,
      changePercent: quote.percent_change || 0,
      volume: quote.volume,
      high: quote.high || quote.price,
      low: quote.low || quote.price,
      open: quote.open || quote.price,
      previousClose: quote.previous_close,
      timestamp: quote.timestamp || Date.now(),
      isMarketOpen: quote.is_market_open,
    };
  }

  // =====================================
  // PORTFOLIO MARKET DATA
  // =====================================

  /**
   * Get market data for portfolio holdings
   */
  async getPortfolioMarketData(holdings: { symbol: string; quantity: number }[]): Promise<PortfolioMarketData> {
    const symbols = holdings.map(h => h.symbol);
    const quotes = await this.getBatchQuotes(symbols);

    const holdingsWithPrices = holdings.map(holding => {
      let quote = quotes.find(q => q.symbol === holding.symbol);
      if (!quote) {
        return {
          symbol: holding.symbol,
          currentPrice: 0,
          previousClose: 0,
          change: 0,
          changePercent: 0,
          volume: 0,
        };
      }

      return {
        symbol: holding.symbol,
        currentPrice: quote.price,
        previousClose: quote.previous_close || quote.price,
        change: quote.change || 0,
        changePercent: quote.percent_change || 0,
        volume: quote.volume,
      };
    });

    const totalValue = holdings.reduce((sum, holding, index) => {
      return sum + (holding.quantity * holdingsWithPrices[index].currentPrice);
    }, 0);

    const previousTotalValue = holdings.reduce((sum, holding, index) => {
      return sum + (holding.quantity * holdingsWithPrices[index].previousClose);
    }, 0);

    const totalChange = totalValue - previousTotalValue;
    const totalChangePercent = previousTotalValue > 0 ? (totalChange / previousTotalValue) * 100 : 0;

    return {
      holdings: holdingsWithPrices,
      totalValue,
      totalChange,
      totalChangePercent,
      lastUpdated: Date.now(),
    };
  }

  // =====================================
  // SIGNAL MARKET CONTEXT
  // =====================================

  /**
   * Get market context for a trading signal
   */
  async getSignalMarketContext(
    symbol: string,
    entryPrice: number,
    targetPrice: number,
    stopLoss: number
  ): Promise<SignalMarketContext> {
    let quote = await this.getRealtimeQuote(symbol);
    const currentPrice = quote.price;

    // Calculate P&L
    const currentPnL = currentPrice - entryPrice;
    const currentPnLPercent = (currentPnL / entryPrice) * 100;

    // Calculate distances
    const distanceToTarget = ((targetPrice - currentPrice) / currentPrice) * 100;
    const distanceToStopLoss = ((currentPrice - stopLoss) / currentPrice) * 100;

    // Determine momentum
    let momentum: 'bullish' | 'bearish' | 'neutral' = 'neutral';
    if (quote.change && quote.change > 0) {
      momentum = 'bullish';
    } else if (quote.change && quote.change < 0) {
      momentum = 'bearish';
    }

    return {
      symbol,
      currentPrice,
      entryPrice,
      targetPrice,
      stopLoss,
      currentPnL,
      currentPnLPercent,
      distanceToTarget,
      distanceToStopLoss,
      volume: quote.volume,
      momentum,
      lastUpdated: Date.now(),
    };
  }

  /**
   * Get enhanced signal context with technical indicators
   */
  async getEnhancedSignalContext(
    symbol: string,
    entryPrice: number,
    targetPrice: number,
    stopLoss: number
  ): Promise<SignalMarketContext> {
    const baseContext = await this.getSignalMarketContext(symbol, entryPrice, targetPrice, stopLoss);

    try {
      // Fetch RSI
      const rsiData = await this.getTechnicalIndicator({
        symbol,
        indicator: 'rsi',
        interval: '1day',
        time_period: 14,
      });

      // Fetch MACD
      const macdData = await this.getTechnicalIndicator({
        symbol,
        indicator: 'macd',
        interval: '1day',
      });

      // Fetch Bollinger Bands
      const bbandsData = await this.getTechnicalIndicator({
        symbol,
        indicator: 'bbands',
        interval: '1day',
        time_period: 20,
      });

      const technicalIndicators: any = {};

      if (rsiData.values && rsiData.values[0]) {
        technicalIndicators.rsi = parseFloat(rsiData.values[0].rsi as string);
      }

      if (macdData.values && macdData.values[0]) {
        technicalIndicators.macd = {
          macd: parseFloat(macdData.values[0].macd as string),
          signal: parseFloat(macdData.values[0].macd_signal as string),
          histogram: parseFloat(macdData.values[0].macd_hist as string),
        };
      }

      if (bbandsData.values && bbandsData.values[0]) {
        technicalIndicators.bbands = {
          upper: parseFloat(bbandsData.values[0].upper_band as string),
          middle: parseFloat(bbandsData.values[0].middle_band as string),
          lower: parseFloat(bbandsData.values[0].lower_band as string),
        };
      }

      return {
        ...baseContext,
        technicalIndicators,
      };
    } catch (error) {
      console.error('Error fetching technical indicators:', error);
      return baseContext;
    }
  }

  /**
   * Calculate technical indicators from candle data
   * Mirrors Convex implementation for compatibility
   */
  public calculateTechnicalIndicators(candles: any[]): {
    rsi: number;
    macd: { macd: number; signal: number; histogram: number };
    sma20: number;
    sma50: number;
    ema20: number;
    bollingerBands: { upper: number; middle: number; lower: number };
  } {
    if (!candles || candles.length === 0) {
      throw new Error('Cannot calculate indicators without candle data');
    }

    const closes = candles.map((c) => c.close);
    const latest = closes[closes.length - 1];

    // RSI Calculation
    const rsi = this.calculateRSI(closes, 14);

    // MACD Calculation
    const ema12 = this.calculateEMA(closes, 12);
    const ema26 = this.calculateEMA(closes, 26);
    const macd = ema12 - ema26;
    const macdSignal = this.calculateEMA([...closes.slice(-9), macd], 9);
    const macdHistogram = macd - macdSignal;

    // Moving averages
    const sma20 = this.calculateSMA(closes, 20);
    const sma50 = this.calculateSMA(closes, Math.min(50, closes.length));
    const ema20 = this.calculateEMA(closes, 20);

    // Bollinger Bands
    const stdDev = this.calculateStdDev(closes.slice(-20), sma20);
    const bollingerBands = {
      upper: sma20 + 2 * stdDev,
      middle: sma20,
      lower: sma20 - 2 * stdDev,
    };

    return {
      rsi,
      macd: { macd, signal: macdSignal, histogram: macdHistogram },
      sma20,
      sma50,
      ema20,
      bollingerBands,
    };
  }

  /**
   * Calculate RSI (Relative Strength Index)
   */
  private calculateRSI(prices: number[], period: number): number {
    if (prices.length < period + 1) return 50;

    let gains = 0;
    let losses = 0;

    for (let i = prices.length - period; i < prices.length; i++) {
      const change = prices[i] - prices[i - 1];
      if (change > 0) gains += change;
      else losses -= change;
    }

    const avgGain = gains / period;
    const avgLoss = losses / period;

    if (avgLoss === 0) return 100;

    const rs = avgGain / avgLoss;
    return 100 - 100 / (1 + rs);
  }

  /**
   * Calculate EMA (Exponential Moving Average)
   */
  private calculateEMA(prices: number[], period: number): number {
    if (prices.length < period) return prices[prices.length - 1];

    const multiplier = 2 / (period + 1);
    let ema = prices.slice(0, period).reduce((a, b) => a + b, 0) / period;

    for (let i = period; i < prices.length; i++) {
      ema = (prices[i] - ema) * multiplier + ema;
    }

    return ema;
  }

  /**
   * Calculate SMA (Simple Moving Average)
   */
  private calculateSMA(prices: number[], period: number): number {
    if (prices.length < period) return prices[prices.length - 1];

    const relevantPrices = prices.slice(-period);
    return relevantPrices.reduce((a, b) => a + b, 0) / period;
  }

  /**
   * Calculate Standard Deviation
   */
  private calculateStdDev(prices: number[], mean: number): number {
    const squaredDiffs = prices.map((price) => Math.pow(price - mean, 2));
    const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / prices.length;
    return Math.sqrt(avgSquaredDiff);
  }

  /**
   * Get historical candles for technical analysis
   * Fetches recent price history needed for indicator calculations
   */
  public async getHistoricalCandles(
    symbol: string,
    timeframe: string,
    count: number = 50
  ): Promise<Array<{
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }>> {
    const cacheKey = `candles_${symbol}_${timeframe}_${count}`;
    const cached = this.getCachedData<any[]>(cacheKey);

    if (cached) {
      console.log(`Cache hit for candles ${symbol}`);
      return cached;
    }

    try {
      // Map timeframe to interval
      const intervalMap: Record<string, string> = {
        '1H': '1h',
        '4H': '4h',
        '1D': '1day',
        '1W': '1week',
        '1M': '1month',
      };

      const interval = (intervalMap[timeframe] || '1h') as any;

      const response = await this.getTimeSeries({
        symbol,
        interval,
        outputsize: count,
      });

      if (!response.values || response.values.length === 0) {
        throw new Error(`No historical data available for ${symbol}`);
      }

      const candles = response.values.map((bar: any) => ({
        time: new Date(bar.datetime).getTime(),
        open: parseFloat(bar.open),
        high: parseFloat(bar.high),
        low: parseFloat(bar.low),
        close: parseFloat(bar.close),
        volume: parseInt(bar.volume || '0'),
      }));

      // Cache for 5 minutes
      this.setCachedData(cacheKey, candles, 300000);

      console.log(`✅ Fetched ${candles.length} candles for ${symbol}`);
      return candles;
    } catch (error) {
      console.error(`Error fetching historical candles for ${symbol}:`, error);
      throw error;
    }
  }

  // =====================================
  // UTILITY METHODS
  // =====================================

  /**
   * Test API connection with multi-provider fallback
   */
  async testConnection(): Promise<{ 
    twelveData: boolean; 
    coinbase: boolean; 
    coingecko: boolean; 
    alphaVantage: boolean;
  }> {
    const results = {
      twelveData: false,
      coinbase: false,
      coingecko: false,
      alphaVantage: false,
    };

    // Test TwelveData
    if (this.apiKey) {
      try {
        await this.makeRequest('/quote', { symbol: 'AAPL' });
        results.twelveData = true;
        console.log('✅ TwelveData API connection successful');
      } catch (error) {
        console.error('❌ TwelveData API connection test failed:', error);
      }
    }

    // Test Coinbase
    if (this.coinbaseApiKey) {
      try {
        await this.getCoinbaseCryptoQuote('BTC/USD');
        results.coinbase = true;
        console.log('✅ Coinbase API connection successful');
      } catch (error) {
        console.error('❌ Coinbase API connection test failed:', error);
      }
    }

    // Test CoinGecko
    try {
      await this.getCoinGeckoCryptoQuote('BTC/USD');
      results.coingecko = true;
      console.log('✅ CoinGecko API connection successful');
    } catch (error) {
      console.error('❌ CoinGecko API connection test failed:', error);
    }

    // Test Alpha Vantage
    if (this.alphaVantageApiKey) {
      try {
        await this.getAlphaVantageQuote('AAPL');
        results.alphaVantage = true;
        console.log('✅ Alpha Vantage API connection successful');
      } catch (error) {
        console.error('❌ Alpha Vantage API connection test failed:', error);
      }
    }

    return results;
  }

  /**
   * Reset rate limit statistics
   */
  public resetRateLimitStats(): void {
    this.rateLimitStats = {
      totalRequests: 0,
      rejectedRequests: 0,
      queuedRequests: 0,
      averageWaitTime: 0,
    };
    console.log('✅ Rate limit statistics reset');
  }

  /**
   * Get circuit breaker status
   */
  public getCircuitBreakerStatus(): {
    state: string;
    consecutiveFailures: number;
    failureThreshold: number;
    lastFailureTime: number;
    timeUntilReset: number;
  } {
    const now = Date.now();
    const timeUntilReset = Math.max(
      0,
      this.circuitBreaker.resetTimeout - (now - this.circuitBreaker.lastFailureTime)
    );

    return {
      state: this.circuitBreaker.state,
      consecutiveFailures: this.circuitBreaker.consecutiveFailures,
      failureThreshold: this.circuitBreaker.failureThreshold,
      lastFailureTime: this.circuitBreaker.lastFailureTime,
      timeUntilReset: timeUntilReset,
    };
  }

  /**
   * Cleanup and disconnect
   */
  public cleanup(): void {
    // Disconnect WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Clear timers
    this.stopHeartbeat();
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }

    // Clear cache
    this.clearCache();

    // Clear subscriptions
    this.wsSubscriptions.clear();
    this.wsCallbacks.clear();

    // Clear request queue and timestamps
    this.requestQueue = [];
    this.requestTimestamps = [];
    this.pendingRequests.clear();

    console.log('✅ Market data service cleaned up');
  }

  /**
   * Deduplicate concurrent requests for the same resource
   * Prevents multiple simultaneous API calls for the same symbol
   */
  private async deduplicateRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check if there's already a pending request for this key
    const existingRequest = this.pendingRequests.get(key);

    if (existingRequest) {
      console.log(`Deduplicating request for ${key} - using existing pending request`);
      return existingRequest as Promise<T>;
    }

    // Create new request and store it
    const newRequest = requestFn().finally(() => {
      // Clean up after request completes
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, newRequest);
    return newRequest;
  }
}

// Export singleton instance
export const marketDataService = new MarketDataService();

// Export class for custom instances
export default MarketDataService;